import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import EnquiryForm from "../components/EnquiryForm";

export default function Retail() {
  const [office, setOffice] = useState(null);

  useEffect(() => {
    api.get("/offices").then((r) => {
      setOffice(r.data.find((o) => o.department?.toLowerCase().includes("retail")) || null);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="retail-page" className="pt-28 sm:pt-36">
      <SEO title="Retail Jewellery & Showroom Appointments — Meerut"
        description="Book an appointment at the Ramesh Kapoor and Sons Jewellers retail showroom in Meerut — wedding, engagement, festival, gifting and personal jewellery." />

      <header className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16">
        <Eyebrow>For Families &amp; Individual Buyers</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4 leading-[1.05] max-w-4xl">
          The Meerut <span className="italic text-[#C5A059]">Retail Showroom</span>
        </h1>
        <p className="text-base text-[#94A3B8] leading-loose mt-8 max-w-2xl">
          Our retail showroom serves individual customers and families. Select jewellery according to your budget,
          occasion, preferred design, and weight — book an appointment and we will prepare a selection before
          your visit.
        </p>
      </header>

      <section className="bg-[#0B1320] border-y border-[#C5A059]/10 py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <Eyebrow>Book an Appointment</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5] mt-3 mb-10">Plan Your Visit</h2>
            <EnquiryForm
              type="retail_appointment"
              fields={[
                { name: "occasion", label: "Occasion", type: "select", required: true,
                  options: ["Wedding", "Engagement", "Anniversary", "Festival", "Gifting", "Personal Purchase", "Gold or Silver Coins"] },
                { name: "budget", label: "Budget", placeholder: "e.g. ₹1–3 lakh" },
                { name: "category", label: "Jewellery category", placeholder: "e.g. bridal set, bangles" },
                { name: "metal", label: "Preferred metal", type: "select", options: ["Gold", "Diamond", "Silver", "Not sure yet"] },
                { name: "weight_range", label: "Preferred weight range", placeholder: "e.g. 20–40 g" },
                { name: "visitors", label: "Number of visitors", type: "number", placeholder: "e.g. 3" },
                { name: "visit_date", label: "Visit date", type: "date", required: true },
                { name: "visit_time", label: "Visit time", type: "time", required: true },
                { name: "requirement", label: "Additional requirement", type: "textarea", wide: true },
              ]}
            />
          </Reveal>
          <div>
            <Reveal delay={0.1} className="aspect-[4/5] overflow-hidden border border-white/10">
              <img src="/images/showroom.jpg" alt="Showroom interior" className="w-full h-full object-cover" />
            </Reveal>
            {office && (
              <Reveal delay={0.15} className="border border-white/10 p-8 mt-6">
                <h3 className="font-serif text-2xl text-[#FBF9F5]">{office.department} — {office.name}</h3>
                <p className="text-sm text-[#94A3B8] mt-3 leading-relaxed">{office.address}</p>
                {(office.phone || office.hours) && (
                  <p className="text-xs text-[#94A3B8] mt-3">
                    {office.phone && <>Phone: {office.phone}</>}{office.phone && office.hours && " · "}{office.hours && <>Hours: {office.hours}</>}
                  </p>
                )}
                {office.maps_url && (
                  <a href={office.maps_url} target="_blank" rel="noreferrer" data-testid="retail-directions-btn"
                    className="inline-block mt-5 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#C5A059] border-b border-[#C5A059]/40 pb-1 hover:border-[#C5A059] transition-colors duration-300">
                    Get Directions →
                  </a>
                )}
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5]">Prefer to browse first?</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/jewellery" data-testid="retail-browse-categories-btn"
              className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-[#C5A059]/60 text-[#C5A059] px-8 py-4 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
              Browse Categories
            </Link>
            <Link to="/jewellery/rates" data-testid="retail-gold-rate-btn"
              className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-white/20 text-[#FBF9F5] px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
              View Today’s Gold Rate
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
